import { Dispatch, useEffect, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import lang from '../../lib/language';
import { TradingPlanStatus } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { searchTradingPlans } from '../../lib/store/crm';
import {
  getAccountSizesToMaxDrawdowns,
  getAllPaidPlans,
  getUniqueAccountSizes,
  getUniqueMaxDrawdowns,
} from '../../lib/store/crm/helpers';
import { ALL_TRANSACTIONS_PAGE_SIZE, FIRST_PAGE_NUMBER } from '../../lib/store/payment/constants';

import { GoBackButton } from '../../components';
import LogoAndCloseButton from '../../components/LogoAndCloseButton/LogoAndCloseButton';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import config from '../../config';
import { GTM_CATEGORIES, GTM_EVENTS, GTM_FUNDED_ACCOUNT } from '../../constants/gtm';
import { hashSHA256 } from '../../util/hash';
import { UI_ROUTES } from '../../util/routes';
import { PaymentMethodSelectionState } from '../Enrollment/PaymentMethodSelection/types';

import PlanDetailsTable from './components/PlanDetailsTable/PlanDetailsTable';
import { autoPassTabs } from './constants';

import styles from './Product.module.scss';

const Product = (props: ReduxProps) => {
  const { tradingPlans, searchPlans, isWebLoginReady, userId, userEmail } = props;

  const accountSizeDefaultIndex = 3;
  const drawdawnPreselectedPercent = 6;
  const navigate = useNavigate();
  const [selectedAccountSizeIndex, setSelectedAccountSizeIndex] = useState<number>(accountSizeDefaultIndex); // $50K default
  const [selectedDrawdownIndex, setSelectedDrawdownIndex] = useState<number>(1); // 6% default
  const [selectedAutoPassIndex, setSelectedAutoPassIndex] = useState(0); // Default to Active

  useEffect(() => {
    if (tradingPlans.length === 0) {
      searchPlans();
    }
  }, [tradingPlans, searchPlans]);

  // Generate dynamic account sizes and drawdowns from available plans
  const dynamicAccountSizes = useMemo(() => getUniqueAccountSizes(tradingPlans), [tradingPlans]);
  const dynamicMaxDrawdowns = useMemo(() => getUniqueMaxDrawdowns(tradingPlans), [tradingPlans]);
  const accountSizesToDrawdowns = useMemo(
    () => getAccountSizesToMaxDrawdowns(tradingPlans),
    [tradingPlans],
  );

  // Synchronize drawdown selection after data loads
  useEffect(() => {
    if (
      dynamicMaxDrawdowns.length > 0
      && selectedAccountSizeIndex !== undefined
      && dynamicAccountSizes.length > 0
    ) {
      const selectedAccountSize = dynamicAccountSizes[selectedAccountSizeIndex];
      const validDrawdowns = accountSizesToDrawdowns[String(selectedAccountSize)] || [];
      const filteredDrawdowns = dynamicMaxDrawdowns.filter((drawdown) =>
        validDrawdowns.includes(drawdown));

      if (filteredDrawdowns.length > 0) {
        const currentDrawdown = dynamicMaxDrawdowns[selectedDrawdownIndex];
        const positionInFiltered = filteredDrawdowns.indexOf(currentDrawdown);

        if (positionInFiltered === -1) {
          // Try to find 6% first (our preferred default with fire emoji)
          const sixPercentIndex = dynamicMaxDrawdowns.findIndex(
            (d) => d === drawdawnPreselectedPercent && filteredDrawdowns.includes(d),
          );
          const targetIndex = sixPercentIndex !== -1
            ? sixPercentIndex
            : dynamicMaxDrawdowns.findIndex((d) => filteredDrawdowns.includes(d));

          if (targetIndex !== -1 && targetIndex !== selectedDrawdownIndex) {
            setSelectedDrawdownIndex(targetIndex);
          }
        }
      }
    }
  }, [
    dynamicMaxDrawdowns,
    dynamicAccountSizes,
    accountSizesToDrawdowns,
    selectedAccountSizeIndex,
    selectedDrawdownIndex,
  ]);

  const accountSizeTabs = useMemo(
    () =>
      dynamicAccountSizes.map((size, index) => ({
        text: index === accountSizeDefaultIndex ? `$${size / 1000}K 🔥` : `$${size / 1000}K`,
        component: null,
      })),
    [dynamicAccountSizes],
  );

  const drawdownPercentageTabs = useMemo(() => {
    // If no account size selected yet, show all options
    if (selectedAccountSizeIndex === undefined || dynamicAccountSizes.length === 0) {
      return dynamicMaxDrawdowns.map((drawdown) => ({
        text: drawdown === drawdawnPreselectedPercent ? `${drawdown}% 🔥` : `${drawdown}%`,
        component: null,
      }));
    }

    // Filter to only show drawdowns valid for selected account size
    const selectedAccountSize = dynamicAccountSizes[selectedAccountSizeIndex];
    const validDrawdowns = accountSizesToDrawdowns[String(selectedAccountSize)] || [];

    return dynamicMaxDrawdowns
      .filter((drawdown) => validDrawdowns.includes(drawdown))
      .map((drawdown) => ({
        text: drawdown === drawdawnPreselectedPercent ? `${drawdown}% 🔥` : `${drawdown}%`,
        component: null,
      }));
  }, [dynamicMaxDrawdowns, selectedAccountSizeIndex, dynamicAccountSizes, accountSizesToDrawdowns]);

  // Filter plans based on user selections
  const selectedPlanChain = useMemo(() => {
    if (dynamicAccountSizes.length === 0 || dynamicMaxDrawdowns.length === 0) {
      return [];
    }

    // Check for undefined indexes
    if (selectedAccountSizeIndex === undefined || selectedDrawdownIndex === undefined) {
      return [];
    }

    const selectedAccountSize = dynamicAccountSizes[selectedAccountSizeIndex];
    const selectedDrawdown = dynamicMaxDrawdowns[selectedDrawdownIndex];

    // Find Challenge phase (first plan in chain)
    const challengePlan = tradingPlans.find(
      (plan) =>
        plan.previous_trading_plan_id === null
        && plan.daily_hard_bp === selectedAccountSize
        && plan.maximum_total_loss === selectedDrawdown,
    );

    if (!challengePlan) {
      return [];
    }

    // Build the chain by following next_trading_plan_id
    const chain: typeof tradingPlans = [challengePlan];

    // eslint-disable-next-line no-constant-condition
    for (let i = 0; i < 10; i += 1) {
      const lastPlan = chain[chain.length - 1];
      if (!lastPlan?.next_trading_plan_id) break;

      const nextPlan = tradingPlans.find((p) => p.id === lastPlan.next_trading_plan_id);
      if (nextPlan) {
        chain.push(nextPlan);
      } else {
        break;
      }
    }

    return chain;
  }, [
    tradingPlans,
    selectedAccountSizeIndex,
    selectedDrawdownIndex,
    dynamicAccountSizes,
    dynamicMaxDrawdowns,
  ]);

  // Map plans to table display format
  const planStages = useMemo(
    () =>
      selectedPlanChain.map((plan) => ({
        name: plan.phase,
        buyingPower: `$${plan.daily_hard_bp?.toLocaleString() || 0}`,
        profitShare: plan.payout_split ? `${plan.payout_split}%` : lang.commonNo(),
        profitTarget: plan.profit_target_amount
          ? `$${plan.profit_target_amount.toLocaleString()}`
          : lang.notAvailable(),
        maxDrawdown: plan.maximum_total_loss_amount
          ? `$${plan.maximum_total_loss_amount.toLocaleString()}`
          : lang.notAvailable(),
        maxDailyLoss: plan.daily_loss_limit_amount
          ? `$${plan.daily_loss_limit_amount.toLocaleString()}`
          : lang.notAvailable(),
        minTradingDays: plan.min_trading_days_target
          ? `${plan.min_trading_days_target} ${lang.commonDays()}`
          : lang.notAvailable(),
        consistencyRule: plan.consistency_coefficient
          ? `${plan.consistency_coefficient}%`
          : lang.notAvailable(),
        riskManagementRule: plan.no_gambling_rule ? lang.commonYes() : lang.commonNo(),
      })),
    [selectedPlanChain],
  );

  // Calculate display price based on AutoPASS selection
  const displayPrice = useMemo(() => {
    if (!selectedPlanChain[0]) return 0;
    return selectedAutoPassIndex === 0
      ? selectedPlanChain[0].plan_price_with_auto_pass
      : selectedPlanChain[0].plan_price;
  }, [selectedPlanChain, selectedAutoPassIndex]);

  const onProductSelect = (id: number) => {
    const withAutoPass = selectedAutoPassIndex === 0;

    if (isWebLoginReady) {
      navigate(
        {
          pathname: UI_ROUTES.paymentMethodSelection,
        },
        {
          state: {
            planId: id,
            prevPage: UI_ROUTES.products,
            withAutoPass,
          } as PaymentMethodSelectionState,
        },
      );
    } else {
      navigate(
        {
          pathname: UI_ROUTES.firstEnroll,
        },
        { state: { id } },
      );
    }
  };

  const SetSelectedAccountSize = (index: number) => {
    setSelectedAccountSizeIndex(index);

    if (selectedDrawdownIndex !== undefined) {
      const newAccountSize = dynamicAccountSizes[index];
      const validDrawdowns = accountSizesToDrawdowns[String(newAccountSize)] || [];
      const currentDrawdown = dynamicMaxDrawdowns[selectedDrawdownIndex];

      // If current drawdown is not valid for new account size, select first valid one
      if (!validDrawdowns.includes(currentDrawdown)) {
        const firstValidDrawdownIndex = dynamicMaxDrawdowns.findIndex((d) =>
          validDrawdowns.includes(d));
        if (firstValidDrawdownIndex !== -1) {
          setSelectedDrawdownIndex(firstValidDrawdownIndex);
        }
      }
    }
  };

  const SetSelectedDrawdownIndex = (index: number) => {
    if (selectedAccountSizeIndex !== undefined) {
      const selectedAccountSize = dynamicAccountSizes[selectedAccountSizeIndex];
      const validDrawdowns = accountSizesToDrawdowns[String(selectedAccountSize)] || [];
      const filteredDrawdowns = dynamicMaxDrawdowns.filter((drawdown) =>
        validDrawdowns.includes(drawdown));
      const selectedDrawdown = filteredDrawdowns[index];
      const fullListIndex = dynamicMaxDrawdowns.indexOf(selectedDrawdown);
      setSelectedDrawdownIndex(fullListIndex);
    } else {
      setSelectedDrawdownIndex(index);
    }
  };

  const handleFundedButton = async () => {
    sessionStorage.removeItem('paymentGatewayURL');
    const plan = selectedPlanChain[0];
    if (!plan) return;
    const withAutoPass = selectedAutoPassIndex === 0;
    const hashedEmail = await hashSHA256(userEmail || '');
    TagManager.dataLayer({
      dataLayer: {
        event: GTM_EVENTS.FUNDED_ACCOUNT_INITIATED,
        event_category: GTM_CATEGORIES.ACCOUNT_MANAGEMENT,
        event_action: GTM_FUNDED_ACCOUNT.ACTION,
        event_label: GTM_FUNDED_ACCOUNT.LABEL,
        client_id: userId,
        user_email_hashed: hashedEmail,
        challenge_id: plan.reference_id,
        challenge_name: plan.name,
        buying_power: plan.daily_hard_bp,
        profit_target: `${plan.profit_target}%`,
        drawdown_percentage: `${plan.maximum_total_loss}%`,
        daily_drawdown_percentage: `${plan.daily_loss_limit}%`,
        auto_advance: withAutoPass,
        paid_price: displayPrice,
      },
    });
    onProductSelect(plan.id);
  };

  return (
    <div className={styles.container}>
      <LogoAndCloseButton
        displayCloseButton
        displayBackButton={false}
        onClose={() => navigate(-1)}
      />
      <div className={styles.goBackBtn}>
        <GoBackButton onBackBtnPress={() => navigate(-1)} />
      </div>
      <div className={styles.wrapper}>
        <div className={styles.choosePlan}>
          <div className={styles.header}>
            <div className={styles.title}>{lang.commonSelectPlan()}</div>
          </div>
          <div className={styles.planSelector}>
            <div className={styles.selector}>
              <div className={styles.smallTitle}>{`${lang.accountSize()}:`}</div>
              <NavButtons
                fullWidthButtons
                tabs={accountSizeTabs}
                sendCurrentTabIndex={SetSelectedAccountSize}
                initialActiveTab={selectedAccountSizeIndex ?? 0}
              />
            </div>
            <div className={styles.selector}>
              <div className={styles.smallTitle}>{`${lang.maxDrawdown()}:`}</div>
              <NavButtons
                fullWidthButtons
                tabs={drawdownPercentageTabs}
                sendCurrentTabIndex={SetSelectedDrawdownIndex}
                initialActiveTab={(() => {
                  // Wait for data to load
                  if (dynamicMaxDrawdowns.length === 0) return 0;
                  if (selectedDrawdownIndex === undefined) return 0;

                  // If account size is selected, calculate position in filtered list
                  if (selectedAccountSizeIndex !== undefined && dynamicAccountSizes.length > 0) {
                    const selectedAccountSize = dynamicAccountSizes[selectedAccountSizeIndex];
                    const validDrawdowns = accountSizesToDrawdowns[String(selectedAccountSize)] || [];
                    const filteredDrawdowns = dynamicMaxDrawdowns.filter((drawdown) =>
                      validDrawdowns.includes(drawdown));
                    const selectedDrawdown = dynamicMaxDrawdowns[selectedDrawdownIndex];
                    const position = filteredDrawdowns.indexOf(selectedDrawdown);
                    return Math.max(0, position); // Never return -1
                  }

                  // No account size selected - use direct index
                  return selectedDrawdownIndex;
                })()}
              />
            </div>
            <div className={styles.selector}>
              <div className={styles.smallTitle}>{lang.autoPass()}</div>
              <NavButtons
                fullWidthButtons
                tabs={autoPassTabs}
                sendCurrentTabIndex={setSelectedAutoPassIndex}
                initialActiveTab={0}
              />
            </div>
            <div className={styles.line} />
            <div className={styles.priceAndButton}>
              <div className={styles.priceContainer}>
                <div className={styles.price}>
                  $
                  {displayPrice?.toFixed(2) || '0.00'}
                </div>
                <div className={styles.text}>{lang.planOneTimeFee()}</div>
              </div>
              <button
                className={styles.button}
                type="button"
                onClick={handleFundedButton}
                disabled={!selectedPlanChain[0]}
              >
                {lang.getFunded()}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.planDetails}>
          <div className={styles.title}>{lang.commonPlanDetails()}</div>
          <div className={styles.planDetailsSection}>
            {planStages.length === 0 ? (
              <div className={styles.noPlansMessage}>{lang.noTradingPlansAvailable()}</div>
            ) : (
              <PlanDetailsTable planStages={planStages} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  tradingPlans: getAllPaidPlans(state.crm),
  isWebLoginReady: state.app.isWebLoginReady,
  userId: state.auth.enroll.userId,
  userEmail: state.crm.individualExtendedInfo?.email,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  searchPlans: () =>
    dispatch(
      searchTradingPlans({
        page_number: FIRST_PAGE_NUMBER,
        page_size: ALL_TRANSACTIONS_PAGE_SIZE,
        order_column: 'ReferenceId',
        order_ascending: true,
        tenant_id: config.auth.tenantId,
        trading_plan_status_enum: TradingPlanStatus.ActiveAndPurchasable,
        work_flow_depth: 3,
      }),
    ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Product);
